import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {GitPullRequestIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Spinner, Tooltip} from '@primer/react'
import {Suspense, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {ClosedByPullRequestsReferencesQuery} from './__generated__/ClosedByPullRequestsReferencesQuery.graphql'
import type {ClosedByPullRequestsReferencesListNode} from './closed-by-pull-requests-references-list-query-types'
import styles from './ClosedByPullRequestsReferences.module.css'
import {
  closedByPullRequestsReferencesQueryKey,
  fetchClosedByPullRequestsReferences,
} from './fetch-closed-by-pull-requests-references'
import {PullRequest, type PullRequestFragment} from './PullRequest'

// Fields read from the parent Issue node. Parent queries inline these fields directly.
// This type uses `| undefined` for Relay compatibility while parent components are still Relay-owned.
export type ClosedByPullRequestsReferencesFragment = {
  readonly closedByPullRequestsReferences:
    | {
        readonly totalCount: number
      }
    | null
    | undefined
}

type Props = {
  issueId: string
  owner: string
  repo: string
  number: number
  closedByPullRequestsReferencesKey: ClosedByPullRequestsReferencesFragment
}

export function ClosedByPullRequestsReferences({
  issueId,
  owner,
  repo,
  number,
  closedByPullRequestsReferencesKey: data,
}: Props) {
  const tanstackEnabled = isFeatureEnabled('closed_by_pull_requests_references_tanstack')
  const totalCount = data?.closedByPullRequestsReferences?.totalCount ?? 0
  const [isClosedByPRRequested, setIsClosedByPRRequested] = useState(false)

  if (totalCount === 0) return null

  const singlePullRequestFallback = (
    <div>
      <GitPullRequestIcon size={16} />
      <span className={styles.pullRequestCountText}>{totalCount.toString()}</span>
    </div>
  )

  const renderPullRequests = (loadingFallback: React.ReactNode) =>
    tanstackEnabled ? (
      <ClosedByPullRequestsReferencesTanstack
        owner={owner}
        repo={repo}
        number={number}
        totalCount={totalCount}
        loadingFallback={loadingFallback}
      />
    ) : (
      <ClosedByPullRequestsReferencesInternal issueId={issueId} totalCount={totalCount} />
    )

  return totalCount === 1 ? (
    <Suspense fallback={singlePullRequestFallback}>
      {isClosedByPRRequested ? (
        renderPullRequests(singlePullRequestFallback)
      ) : (
        <Button
          className={styles.linkedPullRequestsBtn}
          variant="link"
          leadingVisual={GitPullRequestIcon}
          onMouseEnter={() => {
            setIsClosedByPRRequested(true)
          }}
          onFocus={() => {
            setIsClosedByPRRequested(true)
          }}
          aria-label={'1 linked PR'}
        >
          {totalCount.toString()}
        </Button>
      )}
    </Suspense>
  ) : (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip direction="w" text={`${totalCount} linked PRs`} type="description">
          <Button
            variant="link"
            leadingVisual={GitPullRequestIcon}
            className={styles.linkedPullRequestsBtn}
            aria-label={`${totalCount} linked PRs`}
          >
            {totalCount.toString()}
          </Button>
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList className={styles.pullRequestsDropdown}>
          <Suspense fallback={<CenteredLoadingSpinner />}>{renderPullRequests(<CenteredLoadingSpinner />)}</Suspense>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function ClosedByPullRequestsReferencesInternal({issueId, ...props}: {issueId: string; totalCount: number}) {
  const data = useLazyLoadQuery<ClosedByPullRequestsReferencesQuery>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      query ClosedByPullRequestsReferencesQuery($id: ID!) {
        node(id: $id) {
          ... on Issue {
            closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
              edges {
                node {
                  id
                  title
                  number
                  merged
                  closed
                  isDraft
                  repository {
                    name
                    owner {
                      login
                    }
                  }
                  url
                }
              }
            }
          }
        }
      }
    `,
    {
      id: issueId,
    },
  )

  if (!data.node) return <>{'no data'}</>

  // RELAY-CLEANUP(ClosedByPullRequestsReferencesInternal): remove cast when ClosedByPullRequestsReferencesQuery leaves Relay
  const node = data.node as ClosedByPullRequestsReferencesListFragment
  const pullRequests = (node.closedByPullRequestsReferences?.edges || []).flatMap(a => (a && a.node ? a.node : []))

  return <ClosedByPullRequestsReferencesList pullRequests={pullRequests} {...props} />
}

function ClosedByPullRequestsReferencesTanstack({
  owner,
  repo,
  number,
  totalCount,
  loadingFallback,
}: {
  owner: string
  repo: string
  number: number
  totalCount: number
  loadingFallback: React.ReactNode
}) {
  const query = useQuery({
    queryKey: closedByPullRequestsReferencesQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchClosedByPullRequestsReferences(owner, repo, number, signal),
  })

  if (query.isPending) return <>{loadingFallback}</>
  if (query.isError) throw query.error

  const node = query.data.node
  if (!node || !('closedByPullRequestsReferences' in node)) return <>{'no data'}</>

  const pullRequests = (node.closedByPullRequestsReferences?.edges || []).flatMap(a => (a && a.node ? a.node : []))

  return <ClosedByPullRequestsReferencesList pullRequests={pullRequests} totalCount={totalCount} />
}

type ClosedByPullRequestsReferencesListFragment = Pick<
  ClosedByPullRequestsReferencesListNode,
  'closedByPullRequestsReferences'
>

function ClosedByPullRequestsReferencesList({
  pullRequests,
  totalCount,
}: {
  pullRequests: readonly PullRequestFragment[]
  totalCount: number
}) {
  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
      {totalCount === 1 && <PullRequest key={`pr${0}`} pullRequest={pullRequests[0]!} hovercard />}
      {totalCount > 1 && (
        <>
          {pullRequests.map(pr => (
            <PullRequest key={`pr${pr.id}`} pullRequest={pr} />
          ))}
        </>
      )}
    </>
  )
}

function CenteredLoadingSpinner(): React.ReactElement {
  return (
    <div data-testid={'loading-pulls'} className={styles.loadingSpinnerContainer}>
      <Spinner />
    </div>
  )
}
